import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Dialog} from '../../../components/common/dialogs/dialog';
import {DialogContent} from '../../../components/common/dialogs/dialogContent';
import {DialogFooter} from '../../../components/common/dialogs/dialogFooter';
import {DialogHeader} from '../../../components/common/dialogs/dialogHeader';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {
  LocalApplicationServerAuthenticationConfigModel,
  LocalApplicationServerAuthenticationStrategiesEnum,
  LocalApplicationServerModel,
} from '../../../models/api/localApplicationServerModel';
import {createLocalApplicationServerAsyncAction} from '../../../store/data/localApplications/localApplicationsActions';
import {SettingsBannersEnum, useSettingsBanners} from '../../banners/settingsBannersContext';
import {SettingsDevelopersApplicationCreateEditServer} from '../pages/application/servers/settingsDevelopersApplicationCreateEditServerForm';

/*
 * Props.
 */

interface SettingsDevelopersConnectServerDialogProps {
  localApplicationId: number;
  servers: ReadonlyArray<LocalApplicationServerModel>;
  onRequestClose: HandlerOf<string | undefined>;
}

/*
 * Style.
 */

const StyledButtonGroup = styled(ButtonGroup)`
  width: 100%;
`;

const StyledDialog = styled(Dialog)`
  max-height: 80%;
`;

const StyledDialogContent = styled(DialogContent)`
  margin: 0;
  overflow: auto;
  padding: 20px;
`;

/*
 * Component.
 */

export const SettingsDevelopersConnectServerDialog: FC<SettingsDevelopersConnectServerDialogProps> = (
  props,
) => {
  const {localApplicationId, servers, onRequestClose} = props;

  const [origin, setOrigin] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [authConfig, setAuthConfig] = useState<LocalApplicationServerAuthenticationConfigModel>({
    type: LocalApplicationServerAuthenticationStrategiesEnum.NONE,
  });

  const [isConnectingServer, setIsConnectingServer] = useState(false);
  const {showBanner} = useSettingsBanners();
  const dispatch = useAppDispatch();

  const onCloseDialog = () => {
    // If we are connecting, prevent closing.
    if (isConnectingServer) {
      return;
    }
    onRequestClose(undefined);
  };

  const onConnectClickAsync = async () => {
    setIsConnectingServer(true);
    try {
      const server = await dispatch(
        createLocalApplicationServerAsyncAction(localApplicationId, origin, authConfig),
      );
      onRequestClose(server.origin);
    } catch {
      showBanner({
        type: SettingsBannersEnum.ERROR,
        message: (
          <Trans id="settings-developers-connect-server-dialog-error-banner">
            Error connecting server. Please try again.
          </Trans>
        ),
      });
      setIsConnectingServer(false);
    }
  };

  return (
    <StyledDialog onRequestClose={onCloseDialog}>
      <DialogHeader
        onRequestClose={onCloseDialog}
        title={<Trans id="settings-developers-connect-server-dialog-title">Connect server</Trans>}
      />
      <StyledDialogContent>
        <SettingsDevelopersApplicationCreateEditServer
          origin={origin}
          authConfig={authConfig}
          servers={servers}
          onAuthConfigChange={setAuthConfig}
          onOriginChange={setOrigin}
          onFormValidChange={setIsFormValid}
          isInDialog
          requiresHttps
        />
      </StyledDialogContent>
      <DialogFooter>
        <StyledButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={onCloseDialog} isDisabled={isConnectingServer}>
            <Trans id="settings-developers-connect-server-dialog-cancel-button">Cancel</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            onClick={onConnectClickAsync}
            isDisabled={!isFormValid || isConnectingServer}
          >
            <Trans id="settings-developers-connect-server-dialog-connect-button">Connect</Trans>
          </Button>
        </StyledButtonGroup>
      </DialogFooter>
    </StyledDialog>
  );
};
